import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useInvalidatingMutation, useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { reportTaskComplete, reportTaskProgress } from '../../services/taskProgress';
import { notifyInfo } from '../../services/notify';
import type { Collection as DomainCollection, CollectionStatus, CreateCollectionBody } from '../../types/collection';
import type { ProjectDetail } from '../../types/project';

export interface Collection {
  id: string;
  projectId: string;
  name: string;
  status: string;
}

export function useCollections(projectId: string) {
  return useQuery<Collection[]>({
    queryKey: ['collections', projectId],
    queryFn: () => api.get<Collection[]>(`/project/${projectId}/collection`),
    enabled: !!projectId,
  });
}

/** Full detail for one collection (name, custodian, identifiers) — GET /project/:id/collection/:id.
 *  Polls every 5s while the collection is actively processing so the overview's status
 *  strip / progress / gauges advance (mirrors Angular's FeedService live updates). */
export function useCollection(projectId: string, collectionId: string, meta?: Record<string, unknown>) {
  return useQuery<DomainCollection>({
    queryKey: ['collection', projectId, collectionId],
    queryFn: () => api.get<DomainCollection>(`/project/${projectId}/collection/${collectionId}`),
    enabled: !!projectId && !!collectionId,
    meta,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'Processing' || s === 'Initializing' || s === 'New' ? 5000 : false;
    },
  });
}

export function useCreateCollection(projectId: string) {
  return useInvalidatingMutation<DomainCollection, CreateCollectionBody>(
    ['project', projectId],
    (body) => api.post<DomainCollection>(`/project/${projectId}/collection`, body)
  );
}

// Collections render off the project query (['project', projectId]), so resume/rename
// invalidate that key. Errors ride the global handler; only resume has a success toast.
export function useResumeCollection(projectId: string) {
  return useToastMutation(
    ['project', projectId],
    (collectionId: string) => api.post(`/project/${projectId}/collection/${collectionId}/process/resume`),
    {
      success: () => ({ title: 'Resuming collection processing', description: '', timeout: 5000 }),
    }
  );
}

export function useRenameCollection(projectId: string) {
  return useInvalidatingMutation(
    ['project', projectId],
    ({ collection, name }: { collection: DomainCollection; name: string }) =>
      api.put(`/project/${projectId}/collection/${collection.id}`, { ...collection, name })
  );
}

export interface CollectionUpdateMessage {
  projectId: string;
  collectionId: string;
  status?: CollectionStatus;
  numQueued?: number;
  numProcessed?: number;
  bytesProcessed?: number;
  processingSource?: DomainCollection['processingSource'];
  processingOutput?: DomainCollection['processingOutput'];
  percentageComplete: number;
  pdfQueued?: number;
  nlpQueued?: number;
  ocrQueued?: number;
  audioQueued?: number;
}

/**
 * Every collection websocket event: progress bars, the header task dropdown, and the
 * trial-limit pause toast. Mounted once by WebSocketBridge — processing outlives the
 * Collections tab, so these must keep working from anywhere in the app.
 *
 * The Collections tab renders project.collections, so the ['project', id] cache entry is
 * the one to patch. The project comes from each message rather than a prop.
 */
export function useCollectionEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresh = (projectId: string) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['collections', projectId] });
    };

    const offUpdate = onAngularEvent('FeedService.CollectionUpdate', (message: CollectionUpdateMessage) => {
      if (!message?.projectId) return;

      let matched: DomainCollection | undefined;
      queryClient.setQueryData<ProjectDetail>(['project', message.projectId], prev => {
        if (!prev?.collections) return prev;

        const collections = prev.collections.map(c => {
          if (c.id !== message.collectionId) return c;
          matched = c;

          return {
            ...c,
            status: message.status ?? c.status,
            numQueued: message.numQueued ?? c.numQueued,
            numProcessed: message.numProcessed ?? c.numProcessed,
            bytesProcessed: message.bytesProcessed ?? c.bytesProcessed,
            processingSource: message.processingSource ?? c.processingSource,
            processingOutput: message.processingOutput ?? c.processingOutput,
            job: { ...c.job, percentageComplete: message.percentageComplete },
          };
        });

        return matched ? { ...prev, collections } : prev;
      });

      reportTaskProgress({
        id: message.collectionId,
        name: matched?.name ?? 'Collection',
        description: matched ? `Processing: ${matched.name}` : undefined,
        createdAt: matched?.createdAt,
        percentageComplete: message.percentageComplete,
      });

      // Progress for a collection not in the cache — never loaded, or created elsewhere.
      if (!matched) {
        queryClient.invalidateQueries({ queryKey: ['project', message.projectId] });
      }
    });

    // Which cached project holds this collection. ProcessingEnd carries projectId when it
    // arrives on the project topic, but not on every path, so fall back to a cache scan.
    const findProjectId = (collectionId?: string, hinted?: string) => {
      if (hinted) return hinted;
      if (!collectionId) return undefined;
      const entries = queryClient.getQueriesData<ProjectDetail>({ queryKey: ['project'] });
      const hit = entries.find(([, data]) => data?.collections?.some(c => c.id === collectionId));
      return hit?.[1]?.id ?? (hit?.[0]?.[1] as string | undefined);
    };

    // ProcessingEnd is not emitted for collection processing on the current backend (the
    // completion signal is the notification handled below), but it is still broadcast for
    // other flows, so keep honouring it.
    const offEnd = onAngularEvent('FeedService.ProcessingEnd', (message: { id?: string; projectId?: string }) => {
      reportTaskComplete(message?.id);
      const projectId = findProjectId(message?.id, message?.projectId);
      if (projectId) refresh(projectId);
    });

    /**
     * Collection processing finishing emits no domain event — only an account notification
     * ("Processing Complete") relayed by WebSocketService. Progress messages sit at
     * status: 'Processing', pct: 100 for a long stretch before it arrives, so this is the
     * only trustworthy point at which to move the row into the Processed list.
     *
     * The notification carries no collection id; its description is
     * "<collection name> has finished processing under <project name>", so the collection is
     * matched by name against the cached project. A failed match still refetches — the list
     * updates, only the header dropdown task is left to time out.
     */
    const offNotification = onAngularEvent('FeedService.Notification', (message: { title?: string; description?: string; projectId?: string }) => {
      if (message?.title !== 'Processing Complete' || !message.projectId) return;

      const project = queryClient.getQueryData<ProjectDetail>(['project', message.projectId]);
      const description = message.description ?? '';
      // Longest name first: one collection name can be a prefix of another.
      const matched = [...(project?.collections ?? [])]
        .sort((a, b) => (b.name?.length ?? 0) - (a.name?.length ?? 0))
        .find(c => c.name && description.startsWith(`${c.name} has finished processing`));

      if (matched) {
        reportTaskComplete(matched.id);
        // Move the row now rather than waiting on the refetch. The server has already
        // written PROCESSED by the time it sends this, so the refetch confirms rather
        // than contradicts — but the backend emits no websocket status change at all
        // (see the comment above), so without this the list would sit stale for the
        // length of the round trip.
        queryClient.setQueryData<ProjectDetail>(['project', message.projectId], prev =>
          prev?.collections
            ? {
                ...prev,
                collections: prev.collections.map(c =>
                  c.id === matched!.id ? { ...c, status: 'Processed' as CollectionStatus } : c
                ),
              }
            : prev
        );
      }

      refresh(message.projectId);
    });

    const offPaused = onAngularEvent('FeedService.ProcessingPaused', (message: { name: string; projectId?: string }) => {
      notifyInfo({
        title: 'Collection Processing Paused',
        description: `Collection ${message.name} was paused because you've exceeded the allowed 5GB data limit on your trial account, please activate to resume processing.`,
        timeout: 5000,
      });
      if (message.projectId) refresh(message.projectId);
    });

    return () => { offUpdate(); offEnd(); offNotification(); offPaused(); };
  }, [queryClient]);
}
